// lib/koi-pond-v3/geometry/koiV3.ts
//
// T8.1 (rev. 2) — procedural koi geometry. Built in code (no Blender
// pipeline) by parameterizing each anatomical part separately and
// merging into a single BufferGeometry. Body axis is +X (head leads).
//
// REVISION HISTORY
//   rev.1 produced a smooth torpedo that tapered to a solid spike: the
//   "tail + 6 fins" never existed as distinct geometry (zero normal
//   discontinuities). This rev rebuilds form + features while keeping
//   the things rev.1 got right — clean topology, smooth normals, even
//   quad flow, well-behaved nose.
//
// DESIGN (per owner T8.1 revision spec)
//   - Body cross-section is a VERTICAL OVAL (taller than wide), not a
//     circle. width/height ≈ 0.68 at the midsection. Belly is shallower
//     than the back is round (convex dorsal, flat-ish ventral).
//   - Mass is carried AFT: peak girth sits at s≈0.35, fullness holds
//     through the midsection, then tapers — but the peduncle (tail
//     wrist) stays ~20% of peak height. NO spike.
//   - The caudal fin is its OWN thin membrane attached at the peduncle,
//     forked into upper + lower lobes with a notch, tall in Y, razor
//     thin in Z. This is the single highest-impact fix.
//   - Five more fins (dorsal ridge, paired pectorals, paired pelvics,
//     + a small anal fin) each protrude past the body silhouette and
//     remain separate surfaces — visible in both side + top ortho views.
//
// NORMALS
//   Every part calls geometry.computeVertexNormals() BEFORE the merge.
//   Because the parts are separate geometries their vertices are never
//   welded to the body, so the body reads smooth while each fin keeps a
//   crisp normal discontinuity at its seam — which is exactly the proof
//   (in the debug viewer's normals mode) that the fins are real.
//
// VERTEX ATTRIBUTES
//   - position, normal, uv (standard)
//   - bodyFraction: float — 0 at nose, 1 at peduncle, 1.0..1.6 along the
//     caudal fin. T8.3's swim-wave shader reads this to scale per-vertex
//     lateral displacement (more flex toward the tail). Fins INHERIT the
//     bodyFraction of their body attachment point so the same S-wave
//     carries them rigidly instead of tearing them off the hull. The
//     caudal fin alone climbs past 1.0 so it whips with a phase lag.
//   - partIndex: float (0..7) — which anatomical part. T8.2 (pattern
//     masks) + T8.3 (per-part animation curves) switch on this. Note:
//     attachment COHERENCE is carried by bodyFraction, not partIndex —
//     partIndex stays a stable anatomical id so downstream material work
//     has a reliable contract.

import { BufferGeometry, BufferAttribute } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export const KOI_PART = {
  BODY: 0,
  CAUDAL_TAIL: 1,
  DORSAL_FIN: 2,
  PECTORAL_L: 3,
  PECTORAL_R: 4,
  PELVIC_L: 5,
  PELVIC_R: 6,
  ANAL_FIN: 7,
} as const;

// ─────────────────────────────────────────────────────────────────
// Longitudinal layout. Body runs nose (+X) → peduncle; caudal fin
// continues behind the peduncle.
// ─────────────────────────────────────────────────────────────────

const NOSE_X = 0.5;
const PEDUNCLE_X = -0.45; // body length = 0.95
const CAUDAL_TIP_X = -0.72; // caudal length ≈ 0.27 (≈ 28% of body)

// Cross-section peak amplitudes (half-extents at the shoulder).
const PEAK_UPPER = 0.135; // back half-height above the lateral line (convex)
const PEAK_LOWER = 0.105; // belly half-depth below the lateral line (shallow)
const PEAK_WIDTH = 0.082; // side half-width  → W/H = 0.164 / 0.24 ≈ 0.68 oval
const SHOULDER = 0.4; // s of peak girth (pushed back from 0.35 to carry mass aft)
const PEDUNCLE_FLOOR = 0.22; // girth at the peduncle (fraction of peak) — thick wrist
const TAPER_DELAY = 0.15; // rear holds full girth for this fraction before tapering

function bodyX(s: number): number {
  return NOSE_X + (PEDUNCLE_X - NOSE_X) * s;
}

// Longitudinal girth envelope, s∈[0,1]. Peaks at the shoulder, then HOLDS
// fullness through the midsection (taper delayed) before easing into the
// peduncle floor. rev.3 carries noticeably more mass aft than rev.2 —
// the body reads full through the middle rather than bulb-then-cone.
function fullness(s: number): number {
  if (s <= SHOULDER) {
    const t = s / SHOULDER; // 0..1 nose→shoulder
    // rounded snout (non-zero at nose) rising to a full shoulder
    return 0.08 + 0.92 * Math.pow(Math.sin((t * Math.PI) / 2), 0.9);
  }
  const t = (s - SHOULDER) / (1 - SHOULDER); // 0..1 shoulder→peduncle
  // Delay the taper so the midsection stays full, then smoothstep down to
  // the peduncle floor (which stays thick — no spike).
  const td = Math.max(0, (t - TAPER_DELAY) / (1 - TAPER_DELAY));
  const ss = td * td * (3 - 2 * td);
  return PEDUNCLE_FLOOR + (1 - PEDUNCLE_FLOOR) * (1 - ss);
}

const upperHalf = (s: number) => PEAK_UPPER * fullness(s);
const lowerHalf = (s: number) => PEAK_LOWER * fullness(s);
const halfWidth = (s: number) => PEAK_WIDTH * fullness(s);

// ─────────────────────────────────────────────────────────────────
// Small geometry assembly helper. Collects raw arrays, then builds a
// BufferGeometry and computes smooth vertex normals.
// ─────────────────────────────────────────────────────────────────

class PartBuilder {
  positions: number[] = [];
  uvs: number[] = [];
  bodyFractions: number[] = [];
  partIndices: number[] = [];
  indices: number[] = [];

  vertex(
    x: number,
    y: number,
    z: number,
    u: number,
    v: number,
    bf: number,
    part: number,
  ): number {
    const idx = this.positions.length / 3;
    this.positions.push(x, y, z);
    this.uvs.push(u, v);
    this.bodyFractions.push(bf);
    this.partIndices.push(part);
    return idx;
  }

  tri(a: number, b: number, c: number) {
    this.indices.push(a, b, c);
  }

  // grid quad as two triangles (CCW from the front face)
  quad(a: number, b: number, c: number, d: number) {
    this.indices.push(a, b, c);
    this.indices.push(b, d, c);
  }

  finish(): BufferGeometry {
    const geom = new BufferGeometry();
    geom.setAttribute('position', new BufferAttribute(new Float32Array(this.positions), 3));
    geom.setAttribute('uv', new BufferAttribute(new Float32Array(this.uvs), 2));
    geom.setAttribute('bodyFraction', new BufferAttribute(new Float32Array(this.bodyFractions), 1));
    geom.setAttribute('partIndex', new BufferAttribute(new Float32Array(this.partIndices), 1));
    geom.setIndex(this.indices);
    geom.computeVertexNormals();
    return geom;
  }
}

// ─────────────────────────────────────────────────────────────────
// Body — oval cross-sections swept along X, with apex caps at both
// ends so the hull is closed (no open cone tip).
// ─────────────────────────────────────────────────────────────────

function buildBody(q: number): BufferGeometry {
  // Round-2 detail pass: base subdivision raised (16→22 length, 16→18 radial)
  // so the body silhouette curves smoothly at the post-zoom-out distance
  // instead of reading as a faceted blob. The scene is fragment-bound, so the
  // extra vertices are nearly free.
  const LENGTH_SEGS = Math.max(8, Math.round(22 * q));
  const RADIAL_SEGS = Math.max(8, Math.round(18 * q));
  const b = new PartBuilder();

  // Ring grid. theta sweeps the oval: y from sin(theta) (asymmetric
  // upper/lower), z from cos(theta) * halfWidth.
  const ringStart: number[] = []; // first vertex index of each ring
  for (let i = 0; i <= LENGTH_SEGS; i++) {
    const s = i / LENGTH_SEGS;
    const x = bodyX(s);
    const uH = upperHalf(s);
    const lH = lowerHalf(s);
    const w = halfWidth(s);
    ringStart.push(b.positions.length / 3);
    for (let j = 0; j <= RADIAL_SEGS; j++) {
      const theta = (j / RADIAL_SEGS) * Math.PI * 2;
      const st = Math.sin(theta);
      const ct = Math.cos(theta);
      const y = (st >= 0 ? uH : lH) * st; // convex back, shallow belly
      const z = w * ct;
      b.vertex(x, y, z, s, j / RADIAL_SEGS, s, KOI_PART.BODY);
    }
  }
  for (let i = 0; i < LENGTH_SEGS; i++) {
    for (let j = 0; j < RADIAL_SEGS; j++) {
      const a = ringStart[i] + j;
      const bb = a + 1;
      const c = ringStart[i + 1] + j;
      const d = c + 1;
      b.quad(a, bb, c, d);
    }
  }

  // Nose cap — apex slightly forward of the first ring, fanned to it.
  const noseApex = b.vertex(NOSE_X + 0.02, 0, 0, 0, 0.5, 0, KOI_PART.BODY);
  for (let j = 0; j < RADIAL_SEGS; j++) {
    b.tri(noseApex, ringStart[0] + j, ringStart[0] + j + 1);
  }
  // Peduncle cap — rounded stub end (the caudal fin overlaps + hides it).
  const tailApex = b.vertex(PEDUNCLE_X - 0.015, 0, 0, 1, 0.5, 1, KOI_PART.BODY);
  const lastRing = ringStart[LENGTH_SEGS];
  for (let j = 0; j < RADIAL_SEGS; j++) {
    b.tri(tailApex, lastRing + j + 1, lastRing + j);
  }

  return b.finish();
}

// ─────────────────────────────────────────────────────────────────
// Caudal fin — separate thin membrane, forked into upper + lower lobes
// with a centre notch. Tall in Y, near-zero thickness in Z.
//   - For each vertical streamline v∈[-1,1], the reach toward the tip
//     is L(v): outer streamlines (|v|→1) extend to CAUDAL_TIP_X, the
//     middle (v→0) stops short → the V-notch between the two lobes.
//   - Vertical spread grows root→tip, so the lobes splay.
// ─────────────────────────────────────────────────────────────────

function buildCaudalFin(q: number): BufferGeometry {
  const V_SEGS = Math.max(7, Math.round(11 * q)); // across the height (bottom lobe → top lobe)
  const W_SEGS = Math.max(8, Math.round(13 * q)); // root → tip — denser so the tail's sway curves smoothly
  const NOTCH = 0.34; // middle reaches (1 - NOTCH) of full length
  const ROOT_HALF = 0.026; // matches the peduncle half-height (no gap)
  const TIP_HALF = 0.058; // tip-to-tip spread ≈ 0.116
  const b = new PartBuilder();

  const rowStart: number[] = [];
  for (let j = 0; j <= V_SEGS; j++) {
    const v = (j / V_SEGS) * 2 - 1; // -1 bottom .. +1 top
    const L = 1 - NOTCH + NOTCH * Math.pow(Math.abs(v), 1.2); // 0.66..1
    rowStart.push(b.positions.length / 3);
    for (let i = 0; i <= W_SEGS; i++) {
      const w = i / W_SEGS; // 0 root .. 1 tip
      const x = PEDUNCLE_X + (CAUDAL_TIP_X - PEDUNCLE_X) * w * L;
      const halfY = ROOT_HALF + (TIP_HALF - ROOT_HALF) * w;
      const y = v * halfY;
      // faint membrane curl so the fin isn't a perfect plane (helps
      // shading read as a flowing fin rather than a card)
      const z = 0.005 * Math.sin(w * Math.PI) * v;
      const bf = 1.0 + w * 0.6; // 1.0 root → 1.6 tip: max whip + phase lag
      b.vertex(x, y, z, w, (v + 1) / 2, bf, KOI_PART.CAUDAL_TAIL);
    }
  }
  for (let j = 0; j < V_SEGS; j++) {
    for (let i = 0; i < W_SEGS; i++) {
      const a = rowStart[j] + i;
      const bb = a + 1;
      const c = rowStart[j + 1] + i;
      const d = c + 1;
      b.quad(a, bb, c, d);
    }
  }
  return b.finish();
}

// ─────────────────────────────────────────────────────────────────
// Generic leaf fin — a flat triangular membrane in the plane spanned
// by uAxis (root→tip) and vAxis (half-width at the root). Width tapers
// (1-u)^0.7 to a near-point tip. Lives in its own plane so it protrudes
// cleanly past the body and keeps a normal discontinuity at the seam.
// ─────────────────────────────────────────────────────────────────

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

function buildLeafFin(
  origin: Vec3,
  uAxis: Vec3,
  vAxis: Vec3,
  lseg: number,
  hseg: number,
  partIndex: number,
  bodyFraction: number,
): BufferGeometry {
  const b = new PartBuilder();
  const rowStart: number[] = [];
  for (let i = 0; i <= lseg; i++) {
    const u = i / lseg;
    const taper = Math.pow(Math.max(0.04, 1 - u), 0.7);
    const cx = origin.x + uAxis.x * u;
    const cy = origin.y + uAxis.y * u;
    const cz = origin.z + uAxis.z * u;
    rowStart.push(b.positions.length / 3);
    for (let j = 0; j <= hseg; j++) {
      const v = (j / hseg) * 2 - 1; // -1 .. 1 across the width
      const k = v * taper;
      b.vertex(
        cx + vAxis.x * k,
        cy + vAxis.y * k,
        cz + vAxis.z * k,
        u,
        (v + 1) / 2,
        bodyFraction,
        partIndex,
      );
    }
  }
  for (let i = 0; i < lseg; i++) {
    for (let j = 0; j < hseg; j++) {
      const a = rowStart[i] + j;
      const bb = a + 1;
      const c = rowStart[i + 1] + j;
      const d = c + 1;
      b.quad(a, bb, c, d);
    }
  }
  return b.finish();
}

// mirror a fin spec across the Z=0 plane (right → left)
const mirrorZ = (v: Vec3): Vec3 => ({ x: v.x, y: v.y, z: -v.z });

// ─────────────────────────────────────────────────────────────────
// Dorsal fin — a low ridge along the back (s≈0.40→0.70) rising above
// the topline with a gently curved free edge. Thin sheet in the XY
// plane (z≈0) so it breaks the topline silhouette in the side view.
// ─────────────────────────────────────────────────────────────────

function buildDorsalFin(q: number): BufferGeometry {
  const A_SEGS = Math.max(6, Math.round(12 * q)); // along the back
  const H_SEGS = 2; // base → free edge
  const S_START = 0.4;
  const S_END = 0.7;
  const RIDGE_PEAK = 0.05; // height above the topline at mid-span
  const b = new PartBuilder();

  const colStart: number[] = [];
  for (let i = 0; i <= A_SEGS; i++) {
    const a = i / A_SEGS;
    const s = S_START + (S_END - S_START) * a;
    const x = bodyX(s);
    const baseY = upperHalf(s); // sits on the back
    const ridgeH = RIDGE_PEAK * Math.sin(Math.PI * a); // curved, 0 at the ends
    colStart.push(b.positions.length / 3);
    for (let j = 0; j <= H_SEGS; j++) {
      const t = j / H_SEGS; // 0 base .. 1 free edge
      const y = baseY + t * ridgeH;
      b.vertex(x, y, 0, a, t, s, KOI_PART.DORSAL_FIN);
    }
  }
  for (let i = 0; i < A_SEGS; i++) {
    for (let j = 0; j < H_SEGS; j++) {
      const aa = colStart[i] + j;
      const bb = aa + 1;
      const c = colStart[i + 1] + j;
      const d = c + 1;
      b.quad(aa, bb, c, d);
    }
  }
  return b.finish();
}

// ─────────────────────────────────────────────────────────────────
// Top-level: merge all parts into one BufferGeometry.
// ─────────────────────────────────────────────────────────────────

export function makeKoiV3Geometry(opts?: { lowPoly?: boolean; quality?: number }): BufferGeometry {
  // Tessellation factor. lite ≈ 0.6 (mobile, silhouette unchanged at phone
  // size), standard = 1, premium ≈ 1.35 (denser, smoother body). `quality`
  // wins if given; `lowPoly` is the legacy boolean.
  const q = opts?.quality ?? (opts?.lowPoly ? 0.6 : 1);
  const fseg = (n: number) => Math.max(2, Math.round(n * q));
  // Pectoral fins — just behind the gills (s≈0.23), mounted on the
  // flank, swept back + outward + slightly down so the tip clears the
  // body's half-width (≈0.072 here) and reads in the top-down view.
  const pectoralOrigin: Vec3 = { x: 0.27, y: -0.03, z: 0.07 };
  const pectoralU: Vec3 = { x: -0.14, y: -0.045, z: 0.11 }; // tip z ≈ 0.18 ≫ body 0.072
  const pectoralV: Vec3 = { x: 0.05, y: -0.018, z: 0.045 };

  // Pelvic fins — ventral, mid-body (s≈0.58), smaller, angled back.
  const pelvicOrigin: Vec3 = { x: -0.05, y: -0.072, z: 0.045 };
  const pelvicU: Vec3 = { x: -0.1, y: -0.025, z: 0.06 };
  const pelvicV: Vec3 = { x: 0.032, y: -0.01, z: 0.028 };

  const parts: BufferGeometry[] = [
    buildBody(q),
    buildCaudalFin(q),
    buildDorsalFin(q),
    // Pectorals (paired)
    buildLeafFin(pectoralOrigin, pectoralU, pectoralV, fseg(6), fseg(3), KOI_PART.PECTORAL_R, 0.23),
    buildLeafFin(
      mirrorZ(pectoralOrigin),
      mirrorZ(pectoralU),
      mirrorZ(pectoralV),
      fseg(6),
      fseg(3),
      KOI_PART.PECTORAL_L,
      0.23,
    ),
    // Pelvics (paired)
    buildLeafFin(pelvicOrigin, pelvicU, pelvicV, fseg(4), fseg(2), KOI_PART.PELVIC_R, 0.58),
    buildLeafFin(
      mirrorZ(pelvicOrigin),
      mirrorZ(pelvicU),
      mirrorZ(pelvicV),
      fseg(4),
      fseg(2),
      KOI_PART.PELVIC_L,
      0.58,
    ),
    // Anal fin — small, ventral near the tail (s≈0.78), thin in Z so it
    // breaks the bottom line in side view.
    buildLeafFin(
      { x: bodyX(0.78), y: -lowerHalf(0.78), z: 0 },
      { x: -0.08, y: -0.035, z: 0 },
      { x: 0.026, y: -0.008, z: 0 },
      fseg(4),
      fseg(2),
      KOI_PART.ANAL_FIN,
      0.78,
    ),
  ];

  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (!merged) {
    throw new Error('makeKoiV3Geometry: mergeGeometries returned null');
  }
  return merged;
}
